import graphql from "babel-plugin-relay/macro";
import Animated, {
  runOnJS,
  useAnimatedScrollHandler,
  useSharedValue,
  type SharedValue,
} from "react-native-reanimated";
import { useFragment } from "react-relay";

import { VStack } from "@whoosh/design-system/src/components/primitive";
import { getClampedScrollOffsetY } from "@whoosh/design-system/src/patterns/patronApp/MobileHeader/MobileHeader";

import { InstructorListFragment_facility$key } from "./__generated__/InstructorListFragment_facility.graphql";
import { InstructorListFragment_session$key } from "./__generated__/InstructorListFragment_session.graphql";
import { InstructorCard } from "./components/InstructorCard";

type Props = {
  facilityRef: InstructorListFragment_facility$key | null;
  sessionRef: InstructorListFragment_session$key | null;
  setScrolled: (scrolled: boolean) => void;
  scrollY: SharedValue<number>;
};
export const InstructorList = ({
  facilityRef,
  sessionRef,
  setScrolled,
  scrollY,
}: Props) => {
  const session = useFragment(InstructorListSessionFragmentString, sessionRef);
  const facility = useFragment(InstructorListFragmentString, facilityRef);
  const instructors = facility?.department?.instructorProfiles?.edges?.flatMap(
    (e) => (e?.node?.staffProfile ? [e.node.staffProfile] : [])
  );
  const lastScrolled = useSharedValue(false);

  const scrollHandler = useAnimatedScrollHandler({
    onScroll: (event) => {
      const clampedOffsetY = getClampedScrollOffsetY({
        offsetY: event.contentOffset.y,
        contentHeight: event.contentSize?.height,
        viewportHeight: event.layoutMeasurement?.height,
      });
      scrollY.value = clampedOffsetY;
      const nextScrolled = clampedOffsetY > 0;

      if (nextScrolled !== lastScrolled.value) {
        lastScrolled.value = nextScrolled;
        runOnJS(setScrolled)(nextScrolled);
      }
    },
  });

  return (
    <VStack flex={1} backgroundColor="surface-primary">
      <Animated.ScrollView scrollEventThrottle={16} onScroll={scrollHandler}>
        <VStack
          bg="surface-primary"
          pt={{ base: 10, lg: 10 }}
          pb={32} // Lots of padding for signed out banner
          gap={10}
          px={{ base: 0, lg: 10 }}
        >
          {instructors?.map((instructor) => (
            <InstructorCard
              key={instructor.id}
              instructorRef={instructor}
              sessionRef={session ?? null}
              facilityRef={facility ?? null}
            />
          ))}
        </VStack>
      </Animated.ScrollView>
    </VStack>
  );
};

const InstructorListFragmentString = graphql`
  fragment InstructorListFragment_facility on Facility {
    ...InstructorCardFragment_facility
    department {
      instructorProfiles(active: true) {
        edges {
          node {
            staffProfile {
              id
              ...InstructorCardFragment_staffProfile
            }
          }
        }
      }
    }
  }
`;

const InstructorListSessionFragmentString = graphql`
  fragment InstructorListFragment_session on Session {
    ...InstructorCardFragment_session
  }
`;
